import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {Autocomplete, Box, Button, Chip, Link, TextField} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {
  Event,
  EventLink,
  EventStatus,
  formatEventStatus,
  useGetEvents,
} from '@entities/event';
import {MrTable} from '@shared/components';
import {
  CategoryFilter,
  FilterRow,
  SearchFilter,
  SubCategoryFilter,
} from '@shared/components/filters';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__CREATE_EVENT,
  ROUTE__TOPIC_DETAILS,
} from '@shared/constants';
import {
  getStoredCategoryId,
  storeSharedCategoryId,
  useUrlState,
} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {PaginationState} from '@/shared/types';
import {isLive} from '@/shared/utils/dateTime';

const STATUS_OPTIONS = Object.values(EventStatus).map(s => ({
  value: s,
  label: formatEventStatus(s),
}));

const columns: MRT_ColumnDef<Event>[] = [
  {
    accessorKey: 'logoUrl',
    header: 'Logo',
    Cell: ({row}) => {
      const {logoUrl, name} = row.original;

      return logoUrl ? (
        <img
          src={logoUrl}
          width={20}
          height={20}
          alt={name}
          style={{borderRadius: 4, objectFit: 'cover', flexShrink: 0}}
        />
      ) : null;
    },
    enableSorting: false,
    size: 60,
  },
  {
    accessorKey: 'eventId',
    header: 'ID',
    Cell: ({row}) => {
      const {eventId} = row.original;

      return <EventLink eventId={eventId} />;
    },
  },
  {
    accessorKey: 'name',
    header: 'Name',
    Cell: ({row}) => {
      const event = row.original;
      const live = isLive(event.startDate, event.endDate);

      return (
        <Box display="flex" gap={1} alignItems="center">
          {live && (
            <Chip
              label="LIVE"
              color="error"
              size="small"
              sx={{fontWeight: 600}}
            />
          )}
          <span>{event.name}</span>
        </Box>
      );
    },
  },
  {
    accessorKey: 'topic',
    header: 'Topic',
    Cell: ({row}) => {
      const {topic} = row.original;
      if (!topic) return null;
      return (
        <Link
          component={RouterLink}
          to={ROUTE__TOPIC_DETAILS.replace(':topic_id', topic.topicId)}
        >
          {topic.name}
        </Link>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 140,
    Cell: ({row}) => formatEventStatus(row.original.status),
  },
  {
    accessorKey: 'startDate',
    header: 'Start date',
    Cell: ({row}) => formatInTz(row.original.startDate, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'endDate',
    header: 'End date',
    Cell: ({row}) => formatInTz(row.original.endDate, DATETIME_FORMAT),
    size: 160,
  },
];

type UrlState = PaginationState & {
  statuses: EventStatus[];
  categoryId: string | null;
  subCategoryId: string | null;
  searchQuery?: string;
};

export const EventsPage = () => {
  const navigate = useNavigate();
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    statuses: [],
    categoryId: getStoredCategoryId(),
    subCategoryId: null,
  });

  const {data, isPending, error} = useGetEvents({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    searchQuery: urlState.searchQuery ?? undefined,
    statuses: urlState.statuses.length > 0 ? urlState.statuses : undefined,
    categoryId: urlState.categoryId ?? undefined,
    subCategoryId: urlState.subCategoryId ?? undefined,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Box display="flex" alignItems="center" sx={{flexShrink: 0}}>
              <Button
                onClick={() => {
                  navigate(ROUTE__CREATE_EVENT);
                }}
              >
                Create event
              </Button>
            </Box>
            <FilterRow>
              <SearchFilter
                value={urlState.searchQuery}
                onChange={searchQuery =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    searchQuery,
                  }))
                }
              />

              <CategoryFilter
                size="small"
                value={urlState.categoryId}
                sx={{minWidth: 200}}
                onChange={categoryId => {
                  storeSharedCategoryId(categoryId);
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    categoryId,
                    subCategoryId: null,
                  }));
                }}
              />

              <SubCategoryFilter
                size="small"
                categoryId={urlState.categoryId}
                value={urlState.subCategoryId}
                sx={{minWidth: 200}}
                onChange={subCategoryId =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    subCategoryId,
                  }))
                }
              />

              <Autocomplete
                multiple
                size="small"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.filter(o =>
                  urlState.statuses.includes(o.value),
                )}
                getOptionLabel={o => o.label}
                onChange={(_, selected) =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    statuses: selected.map(o => o.value),
                  }))
                }
                renderInput={params => (
                  <TextField {...params} placeholder="Status" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({index})}
                      key={option.value}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
                sx={{minWidth: 200}}
                disableCloseOnSelect
              />
            </FilterRow>
          </Box>
        );
      }}
      enableColumnOrdering
      enableColumnDragging
      enableHiding
      /////////////////////////////
      // pagination + state
      enablePagination
      manualPagination
      enableColumnPinning
      onPaginationChange={updater =>
        setUrl(prev => {
          const nextPagination =
            typeof updater === 'function' ? updater(prev) : updater;

          return {
            ...prev,
            ...nextPagination,
          };
        })
      }
      rowCount={data?.total ?? 0}
      /////////////////////////////
      state={{
        isLoading: isPending,
        pagination: {
          pageIndex: urlState.pageIndex,
          pageSize: urlState.pageSize,
        },
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
